import { cloneDeep, get, last, set, values } from 'lodash';
import { ComponentType, ComponentTypeOption } from '../components/widget/models';
import { ExpressionJson, type DashboardJson } from '../models';
import { Layout, utils } from '@appd/uik';
import { type ActionsRegistryType } from '../actions';
import { getWidgetPropertiesFromTemplate, getWidgetTemplateWithUpdatedWidgetProps } from '../components/widget/utils';

export const EDITOR_CONSTANTS = {
  FILTER_ONCHANGE_CB: "${ function ($v) { $setGlobals('/globals/variables/' & varName,  $v) } }",
};

const generateDashlet = () => {
  return {
    spec: {
      widget: {
        container: {
          name: 'dashify:Widget',
          props: {
            id: '../../../${ name }',
            title: 'New Dashlet',
          },
        },
      },
      view: [['/${ widget.container.name }', '/${ widget.container.props }', [['dashify:Layout', {}, []]]]],
    },
  } as DashboardJson;
};

const generateWidget = () => {
  return {
    spec: {
      widget: {
        container: {
          name: 'dashify:Widget',
          props: {
            id: '../../../${ name }',
            title: 'New Widget',
          },
        },
        component: {
          name: 'editable:TimeSeries',
          props: {
            editable: {
              data: [],
              chartType: 'line',
              xAxisTitle: null,
              yAxisTitle: null,
              stackingType: 'none',
              yAxisType: 'linear',
              legendType: 'bottom',
              tooltipType: 'all',
            },
          },
        },
      },
      view: [
        [
          '/${ widget.container.name }',
          '/${ widget.container.props }',
          [['/${ widget.component.name }', '/${ widget.component.props}']],
        ],
      ],
    },
  };
};

const generateFilter = (name: string) => {
  const varName = last(name.split(':'));
  return {
    spec: {
      widget: {
        component: {
          name: 'editable:Filter',
          props: {
            title: 'Filter Label',
            varName,
            multiple: true,
            filterKey: '',
            options: [],
            editMode: '/${ globals.editMode }',
            value: '/${ ' + `globals.variables.${varName}` + ' }',
            onChange: EDITOR_CONSTANTS.FILTER_ONCHANGE_CB,
          },
        },
      },
      view: [['/${ widget.component.name }', '/${ widget.component.props }']],
    },
  };
};

const generateDashboard = () => {
  return {
    '__META__!': {
      tags: {
        templateType: 'dashboard',
      },
    },
    title: 'New Dashboard',
    templates: [],
    spec: {
      view: [['dashify:Layout', { style: { margin: 16, marginBottom: 64 } }, []]],
    },
  };
};

export const generateTemplate = (name: string, type?: ComponentTypeOption) => {
  let extension = {};

  switch (type) {
    case ComponentType.FILTER:
      extension = generateFilter(name);
      break;
    case ComponentType.WIDGET:
      extension = generateWidget();
      break;
    case ComponentType.DASHLET:
      extension = generateDashlet();
      break;
    default:
      extension = generateDashboard();
      break;
  }

  return {
    ...{
      name,
      spec: {
        datasources: {},
        expressions: {},
        widget: {},
        view: [],
      },
    },
    ...extension,
  } as DashboardJson;
};

/**
 * Check to see if the name includes :{componentType}:
 * and returns that value or defaults to ComponentType.WIDGET.
 *
 * Regex sample:
 * :(filter|widget|dashlet):
 *
 * @param name String
 * @returns ComponentTypeOption String
 */
export const getComponentTypeFromName = (name: string): ComponentTypeOption => {
  const match = name.match(new RegExp(`:(${values(ComponentType).join('|')}):`));
  return get(match, '[1]', ComponentType.WIDGET);
};

export const randomNumber = () => {
  return Math.floor(Math.random() * 99999);
};

export const isRecordOfString = (obj: any): boolean => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  for (const key in obj) {
    if (typeof key !== 'string' || typeof obj[key] !== 'string') {
      return false;
    }
  }

  return true;
};
export const updateExpressionJson = (input: Record<string, string> | Record<string, ExpressionJson>) => {
  if (isRecordOfString(input)) {
    const convertedInput: Record<string, ExpressionJson> = {};
    Object.keys(input).forEach(key => {
      convertedInput[key] = {
        jsonata: input[key],
      };
    });
    return convertedInput;
  }
  return input;
};

export const getTemplateWithUpdatedExpressionFormat = (template: DashboardJson): DashboardJson => {
  const cp = cloneDeep(template) as DashboardJson;

  const expressions = get(cp, 'spec.expressions', {}) as Record<string, unknown>;
  const convertedExpressions = {} as Record<string, ExpressionJson>;
  Object.entries(expressions).forEach(([name, value]) => {
    if (typeof value === 'string') {
      set(convertedExpressions, name, {
        jsonata: value,
        description: '',
        dataSources: [],
      });
    } else {
      set(convertedExpressions, name, value);
    }
  });
  set(cp, 'spec.expressions', convertedExpressions);

  const widgetProps = getWidgetPropertiesFromTemplate(cp);
  if (widgetProps.component.props.editable) {
    const convertedComponentEditableProps = {} as Record<string, unknown>;
    Object.entries(widgetProps.component.props.editable).forEach(([name, value]) => {
      if (typeof value === 'string') {
        if (value.match(/^\/\$\{ expressions\.(.*).jsonata \}$/) || !value.startsWith('/${ expressions.')) {
          set(convertedComponentEditableProps, name, value);
        } else {
          set(convertedComponentEditableProps, name, value.replace(' }', '.jsonata }'));
        }
      } else {
        set(convertedComponentEditableProps, name, value);
      }
    });
    set(widgetProps.component.props, 'editable', convertedComponentEditableProps);
  }

  return getWidgetTemplateWithUpdatedWidgetProps(cp, widgetProps);
};

/** Helpers for manipulating `spec.view`. */
export namespace view {
  export namespace widget {
    export const actions = (
      {
        addWidget: { onClick: addWidget },
        delete: { onClick: $delete },
        duplicate: { onClick: duplicate },
        edit: { onClick: edit },
      }: ActionsRegistryType,
      sync: () => void,
    ): Layout.Widget.Props.Own.Provided & { onAddWidget: () => void } => ({
      onAddWidget: () => (sync(), addWidget()),
      onDelete: (_, layoutId) => (sync(), $delete(Layout.query.id(layoutId)?.parentElement?.id)),
      onDuplicate: (_, layoutId) => (sync(), duplicate(Layout.query.id(layoutId)?.parentElement?.id)),
      onEdit: layoutId => (sync(), edit(Layout.query.id(layoutId)?.parentElement?.id)),
    });

    export const add = (dashboard: DashboardJson, name: string) => {
      // Add new widget element `[name]` as child of "dashify:Layout" element.
      utils.many(get(dashboard, pathTo.children(dashboard, pathTo.LAYOUT), [])).splice(0, 0, [name]);

      // Update "dashify:Layout" element saved prop.
      set(
        dashboard,
        pathTo.prop(dashboard, pathTo.LAYOUT, 'saved'),
        Layout.snapshot.add(
          { parentId: '/0', index: 0 },
          { h: 20, w: '1/2' },
          get(dashboard, pathTo.prop(dashboard, pathTo.LAYOUT, 'saved')) as any,
        ),
      );
    };

    export const duplicate = (dashboard: DashboardJson, name: string, asName: string) => {
      // Locate the existing widget within the view tree.
      const existingPathParts = pathTo(dashboard, name).split('.');

      const [pathToParent, existingIndex] = [
        existingPathParts.slice(0, -1).join('.'),
        +(existingPathParts.at(-1) ?? 0),
      ];

      // Add new widget element `[asName]` as next sibilng of `[name]` widget element.
      utils.many(get(dashboard, pathToParent, [])).splice(existingIndex + 1, 0, [asName]);

      // Find the `<Layout.Widget>` component corresponding to the `[name]` widget element.
      // (Assumes a widget added by editor will have `id` attribute matching its name.)
      const layout = Layout.query.child(document.getElementById(name));

      if (layout) {
        set(
          dashboard,
          // Update "dashify:Layout" element "saved" prop.
          pathTo.prop(dashboard, pathTo.LAYOUT, 'saved'),
          // Compute the resulting snapshot if the duplicate widget is inserted.
          Layout.snapshot.add(
            {
              parentId: layout.api.parent.state?.id ?? '/0',
              index: (layout.api.parent.children.get(layout) ?? 0) + 1,
            },
            { h: 20, w: '1/2' },
            get(dashboard, pathTo.prop(dashboard, pathTo.LAYOUT, 'saved')) as any,
          ),
        );
      }
    };

    export const remove = (dashboard: DashboardJson, name: string) => {
      // Locate the existing widget within the view tree.
      const existingPathParts = pathTo(dashboard, name).split('.');

      const [pathToParent, existingIndex] = [
        existingPathParts.slice(0, -1).join('.'),
        +(existingPathParts.at(-1) ?? 0),
      ];

      // Delete widget element `[name]` wherever it is.
      utils.many(get(dashboard, pathToParent, [])).splice(existingIndex, 1);

      // Find the `<Layout.Widget>` component corresponding to the `[name]` widget element.
      // (Assumes a widget added by editor will have `id` attribute matching its name.)
      const layout = Layout.query.child(document.getElementById(name));

      if (layout) {
        set(
          dashboard,
          // Update "dashify:Layout" element "saved" prop.
          pathTo.prop(dashboard, pathTo.LAYOUT, 'saved'),
          // Compute the resulting snapshot if the widget is deleted.
          Layout.snapshot.delete(
            {
              parentId: layout.api.parent.state?.id ?? '/0',
              index: layout.api.parent.children.get(layout) ?? 0,
            },
            get(dashboard, pathTo.prop(dashboard, pathTo.LAYOUT, 'saved')) as any,
          ),
        );
      }
    };
  }

  /** Returns the lodash get/set path to first element in `view` matching one of `names`, otherwise `spec.view.0`. */
  export const pathTo = (dashboard: DashboardJson, names: utils.ts.OrMany<string>): string =>
    pathTo.fromView(dashboard.spec?.view, utils.many(names), 'spec.view') ?? 'spec.view.0';

  pathTo.LAYOUT = ['dashify:Layout', /** @deprecated */ 'uik:Layout.Provider'];

  /** Shorthand for the child at `index` in children of element at `pathTo(dashboard, names)`. */
  pathTo.at = (dashboard: DashboardJson, names: utils.ts.OrMany<string>, index: number) =>
    `${pathTo.children(dashboard, names)}.${index}`;

  /** Shorthand for children of element at `pathTo(dashboard, names)`. */
  pathTo.children = (dashboard: DashboardJson, names: utils.ts.OrMany<string>) => `${pathTo(dashboard, names)}.2`;

  /** Same as `pathTo(...)` but starting from the `spec.view` of the template. */
  pathTo.fromView = (view: unknown, names: readonly string[], pathSoFar = ''): string | null => {
    if (!Array.isArray(view)) {
      return null;
    }

    for (const [i, child] of view.entries()) {
      const path = [pathSoFar, i].join('.');

      if (names.includes(child?.[0])) {
        return path;
      }

      const fromDescendants = pathTo.fromView(child?.[2], names, [path, 2].join('.'));

      if (fromDescendants) {
        return fromDescendants;
      }
    }

    return null;
  };

  /** Shorthand for given `prop` of element at `pathTo(dashboard, names)`. */
  pathTo.prop = (dashboard: DashboardJson, names: utils.ts.OrMany<string>, prop: string) =>
    `${pathTo(dashboard, names)}.1.${prop}`;

  export const syncLayout = (dashboard: DashboardJson) => {
    set(dashboard, pathTo.prop(dashboard, pathTo.LAYOUT, 'saved'), utils.LOG(Layout.snapshot()));
  };
}
